import { Button, DatePicker, Form, Input, InputNumber, message, Select, Space, Tag } from "antd";
import type { Project, PurchasePayment, PurchasePaymentForm, PurchasePaymentVo } from "@zhihaoscm/entity";
import {
    MODE,
    PERMISSION,
    PROJECT_STATE,
    PURCHASE_PAYMENT_STATE,
    PURCHASE_PAYMENT_TYPE,
    PURCHASE_TYPE,
    ROLE_CODE
} from "@zhihaoscm/meta";
import { dateFormat, dateTimeFormat, formatNumber } from "@zhihaoscm/utils";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { bankService } from "@/api/bankService";
import { projectService } from "@/api/projectService";
import { purchasePaymentService } from "@/api/purchasePaymentService";
import {
    Breadcrumb,
    DetailButtons,
    DetailHeader,
    InfoCard,
    ProTable,
    ZhBaseItem,
    ZhGrid,
    ZhInsetTabs,
    ZhMessageBox,
    type DetailAction,
    type ProColumn
} from "@/components/common";
import { SingleFileUpload, type UploadedFile } from "@/components/media";
import { AccountType, useAccountSession, useUserSession } from "@/stores/session";
import {
    PAYMENT_COLLECTION_STATE,
    PAYMENT_QUERY_TYPE,
    PAYMENT_STATE
} from "../../../../../common/enum/purchasePayment";
import type { CustomerBank } from "../../../../../common/types/customerBank";
import "./console.css";

type PaymentRow = PurchasePayment & Partial<PurchasePaymentVo>;
const paymentStates = PAYMENT_STATE.options.map(item => ({ label: item.label, value: Number(item.value) }));
const collectionStates = PAYMENT_COLLECTION_STATE.options.map(item => ({
    label: item.label,
    value: Number(item.value)
}));

type PaymentActive = "Pay" | "Collect";
/** 收付款列表保留旧 Pay/Collect 视图值、角色分支、身份标签及状态操作。 */
export function PaymentListPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const roleCode = useUserSession(state => state.roleCode) ?? ROLE_CODE.CUSTOMER.code;
    const userInfo = useUserSession(state => state.userInfo);
    const accountType = useAccountSession(state => state.accountType);
    const isCustomer = roleCode === ROLE_CODE.CUSTOMER.code;
    const isSupplier = roleCode === ROLE_CODE.SUPPLIER.code;
    const isSupplyChain = roleCode === ROLE_CODE.SUPPLY_CHAIN.code;
    const [active, setActive] = useState<PaymentActive>(() => {
        const payType = searchParams.get("payType");
        return payType === "Pay" || payType === "Collect" ? payType : isCustomer || isSupplyChain ? "Pay" : "Collect";
    });
    const [refreshKey, setRefreshKey] = useState(0);
    const [confirmDialog, setConfirmDialog] = useState<{
        title?: string;
        tip: string;
        action: () => Promise<unknown>;
    }>();
    const [rejectRow, setRejectRow] = useState<PaymentRow>();
    const [rejectReason, setRejectReason] = useState("");
    const queryType =
        active === "Pay" ? PAYMENT_QUERY_TYPE.PAY_CUSTOMER.code : PAYMENT_QUERY_TYPE.COLLECT_CUSTOMER.code;
    const states = queryType === PAYMENT_QUERY_TYPE.PAY_CUSTOMER.code ? paymentStates : collectionStates;
    const permissions = (userInfo as any).permissions as number[] | undefined;
    const hasPermission =
        accountType !== AccountType.SUB_ACCOUNT || Boolean(permissions?.includes(PERMISSION.PAYMENT.code));
    const reload = () => setRefreshKey(value => value + 1);
    /** 操作成功后刷新当前列表，保持旧 ProTable 操作回调行为。 */
    const run = async (action: () => Promise<unknown>) => {
        await action();
        reload();
    };
    const confirm = (tip: string, action: () => Promise<unknown>, title?: string) =>
        setConfirmDialog({ title, tip, action });
    const reject = (row: PaymentRow) => {
        setRejectReason("");
        setRejectRow(row);
    };
    const operation = (row: PaymentRow) => {
        if (!hasPermission) return "--";
        if (active === "Pay" && row.state === PURCHASE_PAYMENT_STATE.REJECTED.code)
            return (
                <Space className="payment-operation">
                    <Button type="link" onClick={() => navigate(`/console/payment/add?id=${row.id}`)}>
                        修改
                    </Button>
                    <Button
                        danger
                        type="link"
                        onClick={() =>
                            confirm("是否删除，删除后您可以重新发起付款", () => purchasePaymentService.delete(row.id))
                        }
                    >
                        删除
                    </Button>
                </Space>
            );
        if (active === "Pay" && row.state === PURCHASE_PAYMENT_STATE.FINISHED.code)
            return (
                <Button
                    type="link"
                    onClick={() =>
                        confirm(
                            "作废付款需对方确认，对方确认后该付款信息将作废，并重新计算项目相对应金额，是否确认？",
                            () => purchasePaymentService.startInvalid(Number(row.id)),
                            "已发起作废"
                        )
                    }
                >
                    发起作废
                </Button>
            );
        if (active === "Collect" && row.state === PAYMENT_COLLECTION_STATE.CONFIRMING.code)
            return (
                <Space className="payment-operation">
                    <Button
                        type="link"
                        onClick={() =>
                            confirm("是否确认，请仔细核对收款信息", () =>
                                purchasePaymentService.updateState(row.id, PAYMENT_STATE.FINISHED.code)
                            )
                        }
                    >
                        确认
                    </Button>
                    <Button type="link" onClick={() => reject(row)}>
                        驳回
                    </Button>
                </Space>
            );
        if (active === "Collect" && row.state === PURCHASE_PAYMENT_STATE.INVALIDING.code)
            return (
                <Space className="payment-operation">
                    <Button
                        type="link"
                        onClick={() =>
                            confirm("确认后，该付款信息将作废，是否确认？", () =>
                                purchasePaymentService.agreeInvalid(Number(row.id))
                            )
                        }
                    >
                        确认作废
                    </Button>
                    <Button
                        type="link"
                        onClick={() =>
                            confirm("确认后，该作废信息将被驳回，是否确认？", () =>
                                purchasePaymentService.rejectInvalid(Number(row.id))
                            )
                        }
                    >
                        驳回
                    </Button>
                </Space>
            );
        return "--";
    };
    const columns = useMemo<ProColumn<PaymentRow>[]>(
        () => [
            {
                key: "id",
                title: "编号",
                width: 150,
                search: { key: "keyword", type: "input", placeholder: "请输入编号或项目名称" },
                render: (_, row) => (
                    <Button
                        className="payment-code-link"
                        type="link"
                        onClick={() => navigate(`/console/payment/detail?id=${row.id}&payType=${active}`)}
                    >
                        {row.id}
                    </Button>
                )
            },
            { key: "projectName", title: "项目", minWidth: 170 },
            {
                key: "enterprise",
                title: active === "Pay" ? "收款单位" : "付款单位",
                minWidth: 170,
                search: {
                    key: "enterprise",
                    type: "input",
                    placeholder: active === "Pay" ? "请输入收款单位名" : "请输入付款单位名"
                },
                render: (_, row) => {
                    const enterprise = active === "Pay" ? row.collectCustomerEnterprise : row.payCustomerEnterprise;
                    const name =
                        enterprise?.name ||
                        (enterprise as any)?.legalRepresentative ||
                        (enterprise as any)?.realName ||
                        "--";
                    const tag = isSupplyChain
                        ? undefined
                        : active === "Pay"
                          ? row.type === PURCHASE_PAYMENT_TYPE.DOWNSTREAM_UPSTREAM.code
                              ? "供应商"
                              : ROLE_CODE.SUPPLY_CHAIN.intro
                          : row.type === PURCHASE_PAYMENT_TYPE.DOWNSTREAM_UPSTREAM.code
                            ? "采购商"
                            : ROLE_CODE.SUPPLY_CHAIN.intro;
                    return (
                        <span className="payment-counterparty">
                            {name}
                            {tag ? <Tag className="payment-counterparty-tag">{tag}</Tag> : null}
                        </span>
                    );
                }
            },
            { key: "amount", title: "金额(¥)", minWidth: 100, render: (_, row) => formatNumber(row.amount, 2) },
            {
                key: "payDate",
                title: "日期",
                minWidth: 114,
                render: (_, row) => (row.payDate ? dayjs(row.payDate).format("YYYY-MM-DD") : "--")
            },
            {
                key: "state",
                title: "状态",
                minWidth: 72,
                enum: states,
                search: { key: "state", type: "select", placeholder: "请选择状态", options: states }
            },
            { key: "operation", title: "操作", fixed: "right", render: (_, row) => operation(row) }
        ],
        [active, hasPermission, isSupplyChain, navigate, states]
    );
    /** custom-paging 返回 VO 时在页面边界展平。 */
    const request = async (params: Record<string, unknown>) => {
        const page = await purchasePaymentService.paging({ ...params, queryType } as never);
        return {
            total: page.total,
            list: page.list.map(item => {
                const candidate = item as PaymentRow;
                return candidate.purchasePayment ? { ...candidate, ...candidate.purchasePayment } : candidate;
            })
        };
    };
    const pageName = isCustomer ? "付款" : isSupplier ? "收款" : "收付款";
    return (
        <section className="payment-list-page section-card">
            <Breadcrumb routes={[{ name: "采购" }, { name: pageName }]} />
            {isSupplyChain ? (
                <ZhInsetTabs
                    active={active}
                    onChange={setActive}
                    options={[
                        { label: "付款明细", value: "Pay" },
                        { label: "收款明细", value: "Collect" }
                    ]}
                />
            ) : null}
            <div className="payment-list-table">
                <ProTable
                    key={`${active}-${refreshKey}`}
                    title={isSupplyChain ? undefined : `${active === "Pay" ? "付款" : "收款"}列表`}
                    rowKey="id"
                    columns={columns}
                    initialParams={{ queryType }}
                    request={request}
                    header={
                        active === "Pay"
                            ? () => (
                                  <Button type="primary" onClick={() => navigate("/console/payment/add")}>
                                      新增
                                  </Button>
                              )
                            : undefined
                    }
                />
            </div>
            <ZhMessageBox
                open={Boolean(confirmDialog)}
                title={confirmDialog?.title}
                tip={confirmDialog?.tip}
                onCancel={() => setConfirmDialog(undefined)}
                onSubmit={async () => {
                    if (!confirmDialog) return;
                    await run(confirmDialog.action);
                    setConfirmDialog(undefined);
                }}
            />
            <ZhMessageBox
                open={Boolean(rejectRow)}
                title="驳回"
                tip={
                    <div className="payment-reject-box">
                        <p>是否驳回，驳回后该收款信息将不再展示</p>
                        <Input.TextArea
                            value={rejectReason}
                            placeholder="请输入驳回理由"
                            onChange={event => setRejectReason(event.target.value)}
                        />
                    </div>
                }
                onCancel={() => setRejectRow(undefined)}
                onSubmit={async () => {
                    if (!rejectRow || !rejectReason.trim()) {
                        void message.error("请输入驳回理由");
                        return;
                    }
                    await run(() =>
                        purchasePaymentService.reject(rejectRow.id, PAYMENT_STATE.REJECTED.code, {
                            reason: rejectReason.trim()
                        })
                    );
                    setRejectRow(undefined);
                }}
            />
        </section>
    );
}

interface PaymentFormValues extends Omit<PurchasePaymentForm, "payDate"> {
    payDate: dayjs.Dayjs;
}
/** 新增/修改付款单逐项对应旧三列表单、项目/银行摘要与凭证上传。 */
export function PaymentFormPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const id = params.get("id") ?? "";
    const [form] = Form.useForm<PaymentFormValues>();
    const roleCode = useUserSession(state => state.roleCode) ?? ROLE_CODE.CUSTOMER.code;
    const accountId = useAccountSession(state => state.proxyActualAccount.id);
    const isSupplyChain = roleCode === ROLE_CODE.SUPPLY_CHAIN.code;
    const [projects, setProjects] = useState<Project[]>([]);
    const [project, setProject] = useState<Project>();
    const [parties, setParties] = useState<{ payer?: any; acceptor?: any; type?: number }>();
    const [banks, setBanks] = useState<CustomerBank[]>([]);
    const [bank, setBank] = useState<CustomerBank>();
    const [voucher, setVoucher] = useState<UploadedFile>();
    const [submitting, setSubmitting] = useState(false);
    const [submitValues, setSubmitValues] = useState<PaymentFormValues>();
    const loadProjects = async (name?: string) =>
        setProjects(await projectService.noPaging({ states: String(PROJECT_STATE.PROCESSING.code), name } as never));
    useEffect(() => {
        void Promise.all([loadProjects(), bankService.getBankList().then(setBanks)]);
    }, []);
    /** 根据旧 distinguishBothParties 规则计算付款方、收款方和付款类型。 */
    const selectProject = async (projectId: number) => {
        const [data, buyers] = await Promise.all([
            projectService.getInfo(projectId),
            projectService.getBuyers(projectId)
        ]);
        const selected = data.project;
        const buyer =
            roleCode === ROLE_CODE.CUSTOMER.code
                ? buyers.find(item => String(item.buyerId) === String(accountId))
                : buyers[0];
        const direct = PURCHASE_TYPE.DIRECT.match(selected.purType);
        const nextParties =
            roleCode === ROLE_CODE.CUSTOMER.code
                ? {
                      payer: buyer?.buyerEnterprise,
                      acceptor: direct ? selected.supplierEnterprise : selected.supplyChainEnterprise,
                      type: direct
                          ? PURCHASE_PAYMENT_TYPE.DOWNSTREAM_UPSTREAM.code
                          : PURCHASE_PAYMENT_TYPE.DOWNSTREAM_MIDSTREAM.code
                  }
                : direct
                  ? {
                        payer: buyer?.buyerEnterprise,
                        acceptor: selected.supplierEnterprise,
                        type: PURCHASE_PAYMENT_TYPE.DOWNSTREAM_UPSTREAM.code
                    }
                  : {
                        payer: selected.supplyChainEnterprise,
                        acceptor: selected.supplierEnterprise,
                        type: PURCHASE_PAYMENT_TYPE.MIDSTREAM_UPSTREAM.code
                    };
        setProject(selected);
        setParties(nextParties);
        form.setFieldValue("type", nextParties.type);
    };
    useEffect(() => {
        if (!id) return;
        void purchasePaymentService
            .getInfo(id)
            .then(data => {
                const payment = data.purchasePayment;
                setProject(data.project);
                setParties({
                    payer: payment.payCustomerEnterprise,
                    acceptor: payment.collectCustomerEnterprise,
                    type: payment.type
                });
                setBank(
                    payment.bankId
                        ? ({
                              id: payment.bankId,
                              name: payment.bankAccountName,
                              account: payment.bankAccount,
                              bank: payment.bank
                          } as CustomerBank)
                        : undefined
                );
                setVoucher(data.file ? { id: data.file.id, name: data.file.name, path: data.file.path } : undefined);
                form.setFieldsValue({ ...payment, payDate: dayjs(payment.payDate), payCustomerId: Number(accountId) });
            })
            .catch(() => void message.error("加载失败"));
    }, [accountId, form, id]);
    useEffect(() => {
        const projectId = Number(params.get("projectId"));
        if (!id && projectId) {
            form.setFieldValue("projectId", projectId);
            void selectProject(projectId);
        }
    }, [id, params]);
    const submit = async (values: PaymentFormValues) => setSubmitValues(values);
    const tag = (party: "payer" | "acceptor") =>
        isSupplyChain
            ? undefined
            : party === "payer"
              ? roleCode === ROLE_CODE.CUSTOMER.code
                  ? "采购商"
                  : ROLE_CODE.SUPPLY_CHAIN.intro
              : parties?.type === PURCHASE_PAYMENT_TYPE.DOWNSTREAM_UPSTREAM.code
                ? "供应商"
                : roleCode === ROLE_CODE.CUSTOMER.code
                  ? ROLE_CODE.SUPPLY_CHAIN.intro
                  : "供应商";
    const enterpriseName = (value: any) => value?.name || value?.legalRepresentative || value?.realName || "--";
    const bankCard = (value?: string) =>
        value
            ? value
                  .replace(/\s/g, "")
                  .replace(/(.{4})/g, "$1 ")
                  .trim()
            : "--";
    return (
        <section className="payment-form-page">
            <div className="payment-form-card common-card">
                <Breadcrumb
                    routes={[
                        { name: "采购" },
                        { name: "收付款", path: "/console/payment" },
                        { name: id ? "修改付款" : "新增付款" }
                    ]}
                />
                <h1 className="common-title">{id ? "修改付款" : "新增付款"}</h1>
                <Form form={form} layout="vertical" onFinish={submit}>
                    <div className="payment-form-grid">
                        <div>
                            <Form.Item
                                name="projectId"
                                label="关联项目"
                                required
                                rules={[{ required: true, message: "请选择需要关联的项目" }]}
                            >
                                <Select
                                    showSearch
                                    filterOption={false}
                                    disabled={Boolean(id)}
                                    placeholder="请下拉选择或搜索项目"
                                    options={projects.map(item => ({ label: item.name, value: item.id }))}
                                    onSearch={loadProjects}
                                    onChange={selectProject}
                                />
                            </Form.Item>
                            {project && parties ? (
                                <div className="payment-field-summary">
                                    <ZhGrid cols={1}>
                                        <ZhBaseItem label="付款单位">
                                            <>
                                                {enterpriseName(parties.payer)}
                                                {tag("payer") ? (
                                                    <span className="payment-party-tag">{tag("payer")}</span>
                                                ) : null}
                                            </>
                                        </ZhBaseItem>
                                        <ZhBaseItem label="收款单位">
                                            <>
                                                {enterpriseName(parties.acceptor)}
                                                {tag("acceptor") ? (
                                                    <span className="payment-party-tag">{tag("acceptor")}</span>
                                                ) : null}
                                            </>
                                        </ZhBaseItem>
                                    </ZhGrid>
                                </div>
                            ) : null}
                        </div>
                        <Form.Item
                            name="mode"
                            label="方式"
                            required
                            rules={[{ required: true, message: "请选择付款方式" }]}
                        >
                            <Select
                                placeholder="请选择付款方式"
                                options={MODE.options.map(item => ({ label: item.label, value: Number(item.value) }))}
                            />
                        </Form.Item>
                        <div>
                            <Form.Item name="bankId" label="银行账户">
                                <Select
                                    allowClear
                                    placeholder="请选择银行账户"
                                    options={banks.map(item => ({ label: item.bank, value: item.id }))}
                                    onChange={value => {
                                        const selected = banks.find(item => item.id === value);
                                        setBank(selected);
                                    }}
                                />
                            </Form.Item>
                            {bank ? (
                                <div className="payment-field-summary">
                                    <ZhGrid cols={1}>
                                        <ZhBaseItem label="开户名称" value={bank.name} />
                                        <ZhBaseItem label="银行账户" value={bankCard(bank.account)} />
                                        <ZhBaseItem label="开户行" value={bank.bank} />
                                    </ZhGrid>
                                </div>
                            ) : null}
                        </div>
                        <Form.Item
                            name="amount"
                            label="金额"
                            required
                            rules={[{ required: true, message: "请输入付款金额" }]}
                        >
                            <InputNumber
                                min={0.01}
                                precision={2}
                                addonAfter="元"
                                placeholder="请输入付款金额"
                                style={{ width: "100%" }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="payDate"
                            label="付款日期"
                            required
                            rules={[{ required: true, message: "请选择付款日期" }]}
                        >
                            <DatePicker placeholder="请选择付款日期" style={{ width: "100%" }} />
                        </Form.Item>
                        <Form.Item
                            name="payVoucherFileId"
                            hidden
                            rules={[{ required: true, message: "请上传付款凭证" }]}
                        >
                            <InputNumber />
                        </Form.Item>
                        <Form.Item
                            className="payment-voucher-field"
                            label={
                                <span>
                                    凭证
                                    <i
                                        className="iconfont icon-Frame-2"
                                        title="支持上传zip文件和pdf文件，最大不超过10M"
                                    />
                                </span>
                            }
                            required
                            validateStatus={form.getFieldError("payVoucherFileId").length ? "error" : undefined}
                            help={form.getFieldError("payVoucherFileId")[0]}
                        >
                            <SingleFileUpload
                                value={voucher}
                                accept={[".zip", ".pdf"]}
                                maxSizeMb={10}
                                onChange={file => {
                                    setVoucher(file);
                                    form.setFieldValue("payVoucherFileId", file?.id);
                                }}
                            />
                        </Form.Item>
                        <Form.Item className="payment-remark-field" name="remark" label="备注">
                            <Input.TextArea maxLength={200} showCount placeholder="如有其它说明, 请备注" />
                        </Form.Item>
                    </div>
                </Form>
            </div>
            <div className="payment-form-actions">
                <Button type="primary" loading={submitting} onClick={() => form.submit()}>
                    提交
                </Button>
                <Button type="primary" onClick={() => navigate("/console/payment")}>
                    返回
                </Button>
            </div>
            <ZhMessageBox
                open={Boolean(submitValues)}
                tip="是否提交，提交后需等待收款单位确认"
                onCancel={() => setSubmitValues(undefined)}
                onSubmit={async () => {
                    if (!submitValues) return;
                    setSubmitting(true);
                    const payload = {
                        ...submitValues,
                        payCustomerId: Number(accountId),
                        type: parties?.type,
                        bankAccountName: bank?.name,
                        bankAccount: bank?.account,
                        bank: bank?.bank,
                        payDate: submitValues.payDate.format("YYYY-MM-DD HH:mm:ss")
                    };
                    try {
                        if (id) await purchasePaymentService.update(id, payload);
                        else await purchasePaymentService.create(payload);
                        setSubmitValues(undefined);
                        navigate("/console/payment");
                    } finally {
                        setSubmitting(false);
                    }
                }}
            />
        </section>
    );
}

/** 收付款详情逐项对应旧 detail/index.vue，复用统一详情组件与状态操作。 */
export function PaymentDetailPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const id = params.get("id") ?? "";
    const payType: PaymentActive = params.get("payType") === "Collect" ? "Collect" : "Pay";
    const user = useUserSession();
    const accountType = useAccountSession(state => state.accountType);
    const [info, setInfo] = useState<PurchasePaymentVo>();
    const [confirmDialog, setConfirmDialog] = useState<{
        title?: string;
        tip: string;
        action: () => Promise<unknown>;
    }>();
    const [rejecting, setRejecting] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const load = async () => {
        if (!id) return;
        try {
            setInfo(await purchasePaymentService.getInfo(id));
        } catch {
            void message.error("加载失败");
        }
    };
    useEffect(() => {
        void load();
    }, [id]);
    /** 状态动作沿用旧确认文案，成功后重新读取当前详情。 */
    const confirmAction = (content: string, action: () => Promise<unknown>, title = "提示") => {
        setConfirmDialog({ title, tip: content, action });
    };
    /** 收款驳回保留旧版必填理由弹窗。 */
    const reject = () => {
        setRejectReason("");
        setRejecting(true);
    };
    if (!info) return null;
    const payment = info.purchasePayment;
    const isPay = payType === "Pay";
    const subTitle = isPay ? "付款" : "收款";
    const isSupplyChain = user.roleCode === ROLE_CODE.SUPPLY_CHAIN.code;
    const permissions = (user.userInfo as any).permissions as number[] | undefined;
    const hasPermission =
        accountType !== AccountType.SUB_ACCOUNT || Boolean(permissions?.includes(PERMISSION.PAYMENT.code));
    const enterpriseName = (enterprise: any) =>
        enterprise?.name || enterprise?.legalRepresentative || enterprise?.realName || "--";
    /** 付款关系类型固定映射采购商、联合采购商和供应商身份。 */
    const identity = (party: "payer" | "acceptor") => {
        if (isSupplyChain) return undefined;
        if (party === "payer") {
            return payment.type === PURCHASE_PAYMENT_TYPE.MIDSTREAM_UPSTREAM.code
                ? ROLE_CODE.SUPPLY_CHAIN.intro
                : ROLE_CODE.CUSTOMER.intro;
        }
        return payment.type === PURCHASE_PAYMENT_TYPE.DOWNSTREAM_MIDSTREAM.code
            ? ROLE_CODE.SUPPLY_CHAIN.intro
            : ROLE_CODE.SUPPLIER.intro;
    };
    const party = (enterprise: any, tag?: string) => (
        <span className="payment-counterparty">
            {enterpriseName(enterprise)}
            {tag ? <span>{tag}</span> : null}
        </span>
    );
    const bankCard = payment.bankAccount
        ? payment.bankAccount
              .replace(/\s/g, "")
              .replace(/(.{4})/g, "$1 ")
              .trim()
        : "--";
    const actions: DetailAction<PurchasePayment>[] = [
        {
            label: "修改",
            visible: row => isPay && hasPermission && row.state === PURCHASE_PAYMENT_STATE.REJECTED.code,
            action: row => navigate(`/console/payment/add?id=${row.id}`)
        },
        {
            label: "删除",
            visible: row => isPay && hasPermission && row.state === PURCHASE_PAYMENT_STATE.REJECTED.code,
            action: row =>
                confirmAction("是否删除，删除后您可以重新发起付款", async () => {
                    await purchasePaymentService.delete(row.id);
                    navigate("/console/payment");
                })
        },
        {
            label: "发起作废",
            visible: row => isPay && hasPermission && row.state === PURCHASE_PAYMENT_STATE.FINISHED.code,
            action: row =>
                confirmAction(
                    "作废付款需对方确认，对方确认后该付款信息将作废，并重新计算项目相对应金额，是否确认？",
                    () => purchasePaymentService.startInvalid(Number(row.id)),
                    "已发起作废"
                )
        },
        {
            label: "确认",
            visible: row => !isPay && hasPermission && row.state === PAYMENT_COLLECTION_STATE.CONFIRMING.code,
            action: row =>
                confirmAction("是否确认，请仔细核对收款信息", () =>
                    purchasePaymentService.updateState(row.id, PAYMENT_STATE.FINISHED.code)
                )
        },
        {
            label: "驳回",
            visible: row => !isPay && hasPermission && row.state === PAYMENT_COLLECTION_STATE.CONFIRMING.code,
            action: reject
        },
        {
            label: "确认作废",
            visible: row => !isPay && hasPermission && row.state === PURCHASE_PAYMENT_STATE.INVALIDING.code,
            action: row =>
                confirmAction("确认后，该付款信息将作废，是否确认？", () =>
                    purchasePaymentService.agreeInvalid(Number(row.id))
                )
        },
        {
            label: "驳回",
            visible: row => !isPay && hasPermission && row.state === PURCHASE_PAYMENT_STATE.INVALIDING.code,
            action: row =>
                confirmAction("确认后，该作废信息将被驳回，是否确认？", () =>
                    purchasePaymentService.rejectInvalid(Number(row.id))
                )
        },
        { label: "返回", action: () => navigate("/console/payment") }
    ];
    return (
        <section className="payment-detail-page detail-container">
            <DetailHeader
                title={`${subTitle}详情`}
                leftInfos={[{ label: "编号", value: payment.id }]}
                rightInfos={[{ label: "更新时间", value: dateTimeFormat(payment.updatedTime) }]}
                state={{ text: PURCHASE_PAYMENT_STATE.getIntroByCode(payment.state) ?? undefined }}
                menu={
                    <Breadcrumb
                        routes={[
                            { name: "采购" },
                            { name: "收付款", path: "/console/payment" },
                            { name: `${subTitle}明细` }
                        ]}
                    />
                }
            />
            <InfoCard title="基本信息">
                <ZhGrid cols={3}>
                    <ZhBaseItem label="项目">
                        <Button
                            className="payment-project-link"
                            type="link"
                            onClick={() => navigate(`/console/project/detail?id=${payment.projectId}`)}
                        >
                            {payment.projectName || "--"}
                        </Button>
                    </ZhBaseItem>
                    <ZhBaseItem label="付款单位">{party(payment.payCustomerEnterprise, identity("payer"))}</ZhBaseItem>
                    <ZhBaseItem label="收款单位">
                        {party(payment.collectCustomerEnterprise, identity("acceptor"))}
                    </ZhBaseItem>
                    <ZhBaseItem label="方式" value={MODE.getIntroByCode(payment.mode) || "--"} />
                    {payment.bankAccountName ? <ZhBaseItem label="开户名称" value={payment.bankAccountName} /> : null}
                    <ZhBaseItem label="银行账户" value={bankCard} />
                    {payment.bank ? <ZhBaseItem label="开户行" value={payment.bank} /> : null}
                    <ZhBaseItem label="金额">
                        <span className="payment-inline-price">¥{formatNumber(payment.amount, 2)}</span>
                    </ZhBaseItem>
                    <ZhBaseItem label="付款日期" value={dateFormat(payment.payDate)} />
                    <ZhBaseItem label="凭证" fileId={info.file?.id} rewriteFileName="付款凭证" />
                    <ZhBaseItem label="备注" span={3} value={payment.remark || "--"} />
                    {payment.state === PURCHASE_PAYMENT_STATE.REJECTED.code ? (
                        <ZhBaseItem label="驳回理由" span={3} value={payment.reason || "--"} />
                    ) : null}
                </ZhGrid>
            </InfoCard>
            {payment.state === PURCHASE_PAYMENT_STATE.INVALIDING.code ||
            payment.state === PURCHASE_PAYMENT_STATE.INVALID.code ? (
                <InfoCard title="作废信息">
                    <ZhGrid cols={5} gap={16}>
                        <ZhBaseItem label="付款单位确认时间" value={dateTimeFormat(payment.invalidSendTime)} />
                        {payment.state === PURCHASE_PAYMENT_STATE.INVALID.code ? (
                            <ZhBaseItem label="收款单位确认时间" value={dateTimeFormat(payment.invalidConfirmTime)} />
                        ) : null}
                    </ZhGrid>
                </InfoCard>
            ) : null}
            <DetailButtons actions={actions} entity={payment} />
            <ZhMessageBox
                open={Boolean(confirmDialog)}
                title={confirmDialog?.title}
                tip={confirmDialog?.tip}
                onCancel={() => setConfirmDialog(undefined)}
                onSubmit={async () => {
                    if (!confirmDialog) return;
                    await confirmDialog.action();
                    setConfirmDialog(undefined);
                    await load();
                }}
            />
            <ZhMessageBox
                open={rejecting}
                title="驳回"
                tip={
                    <div className="payment-reject-box">
                        <p>是否驳回，驳回后该收款信息将不再展示</p>
                        <Input.TextArea
                            value={rejectReason}
                            placeholder="请输入驳回理由"
                            onChange={event => setRejectReason(event.target.value)}
                        />
                    </div>
                }
                onCancel={() => setRejecting(false)}
                onSubmit={async () => {
                    if (!rejectReason.trim()) {
                        void message.error("请输入驳回理由");
                        return;
                    }
                    await purchasePaymentService.reject(id, PAYMENT_STATE.REJECTED.code, {
                        reason: rejectReason.trim()
                    });
                    setRejecting(false);
                    await load();
                }}
            />
        </section>
    );
}
